/*
================================================================================
FILE HEADER — apps/studio-web/src/extension/bridge.ts
================================================================================

CHANGE LOG
----------
| #  | Date       | Who               | Why               | What Changed             |
|----|------------|-------------------|-------------------|--------------------------|
| 1  | 2026-06-23 | Claude Sonnet 4.6 | Phase 0 bootstrap | Created extension bridge |

FILES THAT IMPORT THIS FILE
-----------------------------
- apps/studio-web/src/App.tsx

EXPORTS COVERED
---------------
- EXTENSION_ID          — resolved from VITE_EXTENSION_ID env var
- ExtensionBridge       — manages chrome.runtime.connect port lifecycle
- isExtensionAvailable() — checks if chrome.runtime.connect is present
- BridgeStatus          — 'disconnected' | 'connecting' | 'connected' | 'error'
- BridgeListener        — (msg: BackgroundToStudio) => void

CALLER → CALLEE MAP
--------------------
ExtensionBridge.connect()    → chrome.runtime.connect()
ExtensionBridge.send()       → port.postMessage()
ExtensionBridge.disconnect() → port.disconnect()
port.onMessage               → notify registered BridgeListeners
port.onDisconnect            → scheduleReconnect()

IMPORTANT POINTS
----------------
1. chrome.runtime.connect() throws if the extension is not installed or the
   extension ID is wrong. Wrap in try/catch and set status to ERROR.
2. The port disconnects automatically when the service worker is terminated.
   We schedule a 2 s reconnect — this wakes the SW and re-establishes the
   port without user intervention.
3. When reconnecting after a SW restart, the caller must re-send ATTACH
   because the SW lost its in-memory activeTabId.
4. Studio must NOT block on extension presence — show ConnectionDoctor instead.
5. In local dev, set VITE_EXTENSION_ID in apps/studio-web/.env.local to the
   unpacked extension's ID from chrome://extensions.

ISSUES FACED / KNOWN GOTCHAS
------------------------------
1. chrome.runtime is undefined in non-Chrome browsers and in some iframes.
   isExtensionAvailable() guards against this.
================================================================================
*/

import type { StudioToBackground, BackgroundToStudio } from './protocol.js';

const EXTENSION_ID: string =
  (import.meta as unknown as { env: Record<string, string> }).env['VITE_EXTENSION_ID'] ?? '';

export type BridgeStatus = 'disconnected' | 'connecting' | 'connected' | 'error';
export type BridgeListener = (msg: BackgroundToStudio) => void;

export function isExtensionAvailable(): boolean {
  return (
    typeof chrome !== 'undefined' &&
    typeof chrome.runtime !== 'undefined' &&
    typeof chrome.runtime.connect === 'function'
  );
}

export class ExtensionBridge {
  private port: chrome.runtime.Port | null = null;
  private listeners = new Set<BridgeListener>();
  private statusListeners = new Set<(s: BridgeStatus) => void>();
  private _status: BridgeStatus = 'disconnected';
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;

  get status(): BridgeStatus {
    return this._status;
  }

  private setStatus(s: BridgeStatus): void {
    this._status = s;
    this.statusListeners.forEach((fn) => fn(s));
  }

  connect(): void {
    if (!isExtensionAvailable()) {
      this.setStatus('error');
      return;
    }
    if (this._status === 'connected' || this._status === 'connecting') return;

    this.setStatus('connecting');

    console.log('[Plex bridge] connecting, EXTENSION_ID =', EXTENSION_ID || '(none)');
    try {
      this.port = EXTENSION_ID
        ? chrome.runtime.connect(EXTENSION_ID, { name: 'plex-studio' })
        : chrome.runtime.connect({ name: 'plex-studio' });
      this.setStatus('connected');
      console.log('[Plex bridge] port connected');

      this.port.onMessage.addListener((msg: BackgroundToStudio) => {
        this.listeners.forEach((fn) => fn(msg));
      });

      this.port.onDisconnect.addListener(() => {
        const err = chrome.runtime.lastError;
        console.log('[Plex bridge] port disconnected, lastError =', err?.message ?? 'none');
        this.port = null;
        this.setStatus('disconnected');
        // Reconnect after 2 s — SW may have been restarted
        this.reconnectTimer = setTimeout(() => this.connect(), 2000);
      });
    } catch (err) {
      console.error('[Plex bridge] connect threw:', err);
      this.setStatus('error');
    }
  }

  disconnect(): void {
    if (this.reconnectTimer !== null) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    this.port?.disconnect();
    this.port = null;
    this.setStatus('disconnected');
  }

  send(msg: StudioToBackground): void {
    this.port?.postMessage(msg);
  }

  on(listener: BridgeListener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  onStatus(listener: (s: BridgeStatus) => void): () => void {
    this.statusListeners.add(listener);
    return () => this.statusListeners.delete(listener);
  }
}
